import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { TrendingIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

type ShareBandStepVariant = 'outline' | 'primary';

// Decorative connector between share-band steps. Shaft and arrowhead are one SVG rather
// than a rule plus an arrow icon, so they can't drift apart into a seam. The outer <svg>
// has no `viewBox`, so its units are CSS pixels; the arrowhead is nested with a matching
// `viewBox` so it keeps its shape, and sits at `x="100%"` shifted back by its own width.
// Hidden below `lg`, where the band stacks into a column.
const CONNECTOR_HEIGHT = 16;
const ARROWHEAD_WIDTH = 10;
const CONNECTOR_MID = CONNECTOR_HEIGHT / 2;

const ShareBandConnector: React.FC<{ dashed?: boolean }> = ({ dashed }) => (
	<svg
		aria-hidden="true"
		data-testid="share-band-connector"
		// Fixed width and `shrink-0` so all three stay identical — flexing makes each give up a
		// different amount, since each shares a row with a different length of copy. The row's
		// free space goes to the `<li>`s' own `flex-1`, which is what keeps the columns equal.
		className="text-stroke-weaker mr-4 hidden h-4 w-6 shrink-0 lg:mr-6 lg:block"
	>
		{dashed ? (
			<line
				x1="0"
				y1={CONNECTOR_MID}
				x2="100%"
				y2={CONNECTOR_MID}
				stroke="currentColor"
				strokeWidth={2}
				strokeLinecap="round"
				strokeDasharray="1 5"
			/>
		) : (
			<rect x="0" y={CONNECTOR_MID - 1} width="100%" height={2} fill="currentColor" />
		)}
		<svg
			x="100%"
			y="0"
			width={ARROWHEAD_WIDTH}
			height={CONNECTOR_HEIGHT}
			viewBox={`0 0 ${ARROWHEAD_WIDTH} ${CONNECTOR_HEIGHT}`}
			transform={`translate(-${ARROWHEAD_WIDTH}, 0)`}
		>
			<path
				d={`M0 ${CONNECTOR_MID - 5} L${ARROWHEAD_WIDTH} ${CONNECTOR_MID} L0 ${CONNECTOR_MID + 5} Z`}
				fill="currentColor"
			/>
		</svg>
	</svg>
);

const ShareBandStepMarker: React.FC<{ number: number; variant: ShareBandStepVariant }> = ({ number, variant }) => (
	<Text
		aria-hidden="true"
		as="span"
		size="small"
		fontWeight="bold"
		className={cn(
			'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
			variant === 'primary'
				? 'bg-fill-brand-strong text-typography-inverse-strong-fixed'
				: 'border-stroke-strong text-typography-strong border-2',
		)}
	>
		{number}
	</Text>
);

/**
 * Renders the final deliverable's marker: a bare trending icon with no circle, deliberately
 * smaller (24px) than the numbered circles — without a circle around it, matching their 32px reads
 * oversized.
 *
 * `ms-1` makes up the 4px difference between this row's marker column (24px icon + 16px gap) and
 * the numbered rows' (32px circle + 12px gap), so all four labels share a left edge once the band
 * stacks. Dropped again at `lg`, where the band is a row and there is nothing to align to.
 */
const ShareBandFinalMarker: React.FC = () => (
	<TrendingIcon
		aria-hidden="true"
		data-testid="share-band-final-icon"
		size="medium"
		className="text-stroke-brand-strong ms-1 shrink-0 lg:ms-0"
	/>
);

type ShareBandStepContentProps = Readonly<{
	number: number;
	variant: ShareBandStepVariant;
	label: React.ReactNode;
	sub: React.ReactNode;
}>;

const ShareBandStepContent: React.FC<ShareBandStepContentProps> = ({ number, variant, label, sub }) => (
	<div className="flex items-center gap-3">
		<ShareBandStepMarker number={number} variant={variant} />
		<div>
			<Text as="p" size="small" fontWeight="bold">
				{label}
			</Text>
			<Text as="p" size="x-small" className="text-typography-weak mt-0.5">
				{sub}
			</Text>
		</div>
	</div>
);

export const Deliverables: React.FC = () => {
	return (
		<section id="deliverables" className="bg-background-raised py-16">
			<div className="container">
				<Text as="p" size="small" fontWeight="bold" className="text-stroke-brand-strong mb-2 tracking-wide uppercase">
					<Translate value="corgi.ambassadors.deliverables.label" />
				</Text>
				<Text as="h2" variant="heading" size="large">
					<Translate value="corgi.ambassadors.deliverables.heading" />
				</Text>
				<Text as="p" size="large" className="text-typography-weak mt-2">
					<Translate value="corgi.ambassadors.deliverables.sub" />
				</Text>

				<ol className="border-stroke-weaker bg-background-base rounded-large mt-8 flex flex-col gap-6 border p-6 lg:flex-row lg:items-center lg:gap-0 lg:p-8">
					<li className="flex flex-1 items-center">
						<ShareBandStepContent
							number={1}
							variant="primary"
							label={<Translate value="corgi.ambassadors.deliverables.share_band.step_1.label" />}
							sub={<Translate value="corgi.ambassadors.deliverables.share_band.step_1.sub" />}
						/>
					</li>
					<li className="flex flex-1 items-center">
						<ShareBandConnector />
						<ShareBandStepContent
							number={2}
							variant="outline"
							label={<Translate value="corgi.ambassadors.deliverables.share_band.step_2.label" />}
							sub={<Translate value="corgi.ambassadors.deliverables.share_band.step_2.sub" />}
						/>
					</li>
					<li className="flex flex-1 items-center">
						<ShareBandConnector />
						<ShareBandStepContent
							number={3}
							variant="outline"
							label={<Translate value="corgi.ambassadors.deliverables.share_band.step_3.label" />}
							sub={<Translate value="corgi.ambassadors.deliverables.share_band.step_3.sub" />}
						/>
					</li>
					{/* `flex-1` like the numbered steps rather than sized to its own content, so the label
					    wraps on the same terms they do instead of holding the row open on one line. */}
					<li className="flex flex-1 items-center">
						<ShareBandConnector dashed />
						{/* A wider gap than the numbered steps use: their circles give the label consistent
						    breathing room that this bare glyph doesn't have on its own. */}
						<div className="flex items-center gap-4">
							<ShareBandFinalMarker />
							<Text as="p" size="medium" fontWeight="bold" className="text-stroke-brand-strong">
								<Translate value="corgi.ambassadors.deliverables.share_band.final_label" />
							</Text>
						</div>
					</li>
				</ol>

				<Text as="p" size="medium" className="text-typography-weak border-stroke-weaker mt-6 border-t pt-4">
					<Translate value="corgi.ambassadors.deliverables.note" />
				</Text>
			</div>
		</section>
	);
};
